<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">

    <!-- Reset control -->
    <div class="filter my-3">
      <div class="filter-head">
        <h4>إعادة تسجيل الطلاب</h4>
      </div>
      <p class="text-muted mb-3">
        إعادة التسجيل تُنشئ دورة تسجيل جديدة للمرحلة المحددة وتطلب من طلابها تسجيل الدخول مرة أخرى.
        لا يتم حذف أي بيانات أو سجلات سابقة.
      </p>
      <div class="row align-items-end">
        <div class="col-lg-5 col-md-8 col-12">
          <div class="form-group d-flex flex-column">
            <label>المرحلة التعليمية</label>
            <Dropdown v-model="academicYearId" :options="yearOptions" optionLabel="name" optionValue="id"
                      placeholder="اختر المرحلة" />
          </div>
        </div>
        <div class="col-lg-4 col-md-4 col-12">
          <button class="btn btn-danger w-100" :disabled="!academicYearId || previewing" @click="openConfirm">
            <i class="pi pi-refresh"></i>
            {{ previewing ? 'جارٍ الحساب…' : 'إعادة تسجيل الطلاب' }}
          </button>
        </div>
      </div>

      <!-- Success banner -->
      <div v-if="lastResult" class="alert alert-success mt-3 mb-0">
        تمت إعادة تسجيل <strong>{{ lastResult.students_affected }}</strong> طالب في مرحلة
        <strong>{{ lastResult.academic_year_name }}</strong>.
        جلسة التسجيل الجديدة: <strong>#{{ lastResult.registration_session_id }}</strong>.
      </div>
    </div>

    <!-- Sessions inspector -->
    <div class="filter my-3">
      <div class="filter-head">
        <h4>جلسات التسجيل ({{ sessions.total ?? 0 }})</h4>
      </div>
    </div>
    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>رقم الجلسة</th>
            <th>المرحلة التعليمية</th>
            <th>عدد الطلاب المتأثرين</th>
            <th>أنشئت بواسطة</th>
            <th>تاريخ الإنشاء</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="s in sessions.data" :key="s.id">
            <td>#{{ s.id }}</td>
            <td>{{ s.academic_year_name ?? '-' }}</td>
            <td>{{ s.students_affected }}</td>
            <td>{{ s.created_by_name ?? 'النظام' }}</td>
            <td class="text-nowrap">{{ formatDate(s.created_at) }}</td>
          </tr>
          <tr v-if="!sessions.data || sessions.data.length === 0">
            <td colspan="5" class="text-center py-4">لا توجد جلسات إعادة تسجيل بعد</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="row" v-if="sessions.total">
      <div class="col-12">
        <pagination :data="sessions" class="mx-auto d-flex align-items-center justify-content-center pagination"
                    @pagination-change-page="fetchSessions">
          <template v-slot:prev-nav>&lt;</template>
          <template v-slot:next-nav>&gt;</template>
        </pagination>
      </div>
    </div>

    <!-- Confirmation modal -->
    <div v-if="confirmOpen" class="ra-modal-backdrop" @click.self="confirmOpen = false">
      <div class="ra-modal">
        <h5 class="mb-3">تأكيد إعادة التسجيل</h5>
        <p class="mb-2">
          سيتم إعادة تسجيل جميع طلاب المرحلة المحددة، وسيُطلب منهم التسجيل مرة أخرى. هل أنت متأكد؟
        </p>
        <ul class="mb-3">
          <li>المرحلة: <strong>{{ selectedYearName }}</strong></li>
          <li>عدد الطلاب المتأثرين: <strong>{{ previewCount }}</strong></li>
        </ul>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-secondary" :disabled="submitting" @click="confirmOpen = false">إلغاء</button>
          <button class="btn btn-danger" :disabled="submitting" @click="doReset">
            {{ submitting ? 'جارٍ التنفيذ…' : 'تأكيد إعادة التسجيل' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Dropdown from 'primevue/dropdown';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';

export default {
  name: 'RegistrationReset',
  components: { Breadcrumb, Dropdown, pagination },
  data() {
    return {
      yearOptions: [],
      academicYearId: null,
      previewing: false,
      previewCount: 0,
      confirmOpen: false,
      submitting: false,
      lastResult: null,
      sessions: { data: [], total: 0 },
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'إعادة تسجيل الطلاب', path: '/students/registration-reset' },
      ],
    };
  },
  computed: {
    selectedYearName() {
      const y = this.yearOptions.find(o => o.id === this.academicYearId);
      return y ? y.name : '';
    },
  },
  methods: {
    formatDate(iso) {
      if (!iso) return '-';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('ar-EG');
    },
    // Fetch the affected count first so the dialog shows a real number before committing.
    openConfirm() {
      if (!this.academicYearId) return;
      this.previewing = true;
      axios.post('/admin/registration/reset-preview', { academic_year_id: this.academicYearId })
        .then(({ data }) => {
          this.previewCount = data.students_affected ?? 0;
          this.confirmOpen = true;
        })
        .catch(() => { this.previewCount = 0; this.confirmOpen = true; })
        .finally(() => { this.previewing = false; });
    },
    doReset() {
      this.submitting = true;
      axios.post('/admin/registration/reset', { academic_year_id: this.academicYearId })
        .then(({ data }) => {
          this.lastResult = data;
          this.confirmOpen = false;
          this.fetchSessions(1);
        })
        .catch(() => { /* keep the modal open on failure */ })
        .finally(() => { this.submitting = false; });
    },
    fetchSessions(page = 1) {
      axios.get('/admin/registration/sessions', { params: { page } }).then(({ data }) => {
        this.sessions = data.data;
      }).catch(() => { this.sessions = { data: [], total: 0 }; });
    },
    fetchYears() {
      axios.get('/academic-years').then(({ data }) => {
        this.yearOptions = data.data || [];
      }).catch(() => { this.yearOptions = []; });
    },
  },
  mounted() {
    this.fetchYears();
    this.fetchSessions();
  },
};
</script>

<style scoped>
.ra-modal-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1050;
}
.ra-modal {
  background: #fff; border-radius: 12px; padding: 1.5rem;
  width: 100%; max-width: 460px; direction: rtl;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}
.gap-2 { gap: 0.5rem; }
</style>
